import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check, MessageCircle, Receipt, UserPlus, Star, ShoppingBag, Radio, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import api from "../lib/api";
import { toast } from "sonner";
import SwipeRow from "./SwipeRow";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return `${Math.floor(d / 7)}w`;
}

const TYPE_META = {
  dm_new: { Icon: MessageCircle, color: "text-pink-300 bg-pink-500/15 border-pink-400/30" },
  order_new: { Icon: ShoppingBag, color: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  order_status: { Icon: Receipt, color: "text-cyan-200 bg-cyan-400/15 border-cyan-400/30" },
  follow_new: { Icon: UserPlus, color: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  review_new: { Icon: Star, color: "text-yellow-200 bg-yellow-400/15 border-yellow-400/30" },
  vendor_live: { Icon: Radio, color: "text-red-200 bg-red-500/15 border-red-400/40" },
};

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastSeenCount = useRef(0);
  const navigate = useNavigate();

  const tick = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    tick();
    const i = setInterval(tick, 15000);
    return () => clearInterval(i);
  }, [tick]);

  // Small ring animation when count grows
  useEffect(() => {
    if (count > lastSeenCount.current && lastSeenCount.current >= 0) {
      // count went up
    }
    lastSeenCount.current = count;
  }, [count]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadList();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { ids: null });
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
      setCount(0);
      toast.success("All notifications marked read");
    } catch {
      toast.error("Couldn't mark read");
    }
  };

  const openOne = async (n) => {
    if (!n.is_read) {
      try {
        await api.post("/notifications/mark-read", { ids: [n.notification_id] });
        setItems((arr) => arr.map((x) => (x.notification_id === n.notification_id ? { ...x, is_read: true } : x)));
        setCount((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const deleteOne = async (n) => {
    try {
      await api.delete(`/notifications/${n.notification_id}`);
      setItems((arr) => arr.filter((x) => x.notification_id !== n.notification_id));
      if (!n.is_read) setCount((c) => Math.max(0, c - 1));
      toast.success("Moved to Trash");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const BellIcon = count > 0 ? BellRing : Bell;

  return (
    <>
      <button
        onClick={() => onOpenChange(true)}
        className="relative h-10 w-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <BellIcon className={`w-5 h-5 ${count > 0 ? "text-pink-300" : ""}`} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-pink-500 text-[10px] font-bold grid place-items-center glow-pink"
            data-testid="notification-unread-badge"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md"
          data-testid="notification-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-white text-xl font-black tracking-tighter flex items-center gap-2">
              <Bell className="w-5 h-5 text-pink-300" /> Notifications
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-zinc-400">
              {count > 0 ? `${count} unread` : "All caught up"}
            </p>
            {count > 0 && (
              <button
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-xs font-bold text-pink-300 hover:text-pink-200"
                data-testid="notification-mark-all-read"
              >
                <Check className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="mt-4 space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(100dvh - 140px)" }}>
            {loading && <p className="text-xs text-zinc-500">Loading…</p>}
            {!loading && items.length === 0 && (
              <div className="text-center py-16 text-zinc-500" data-testid="notification-empty">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet.</p>
                <p className="text-xs mt-1">DMs, orders, follows and reviews will show up here.</p>
              </div>
            )}
            {items.map((n) => {
              const meta = TYPE_META[n.type] || { Icon: Bell, color: "text-zinc-300 bg-white/5 border-white/10" };
              const { Icon } = meta;
              return (
                <SwipeRow
                  key={n.notification_id}
                  onDelete={() => {
                    if (window.confirm("Delete this notification?")) deleteOne(n);
                  }}
                  testIdPrefix={`notif-${n.notification_id}`}
                >
                  <div
                    onClick={() => openOne(n)}
                    role="button"
                    tabIndex={0}
                    className={`w-full text-left p-3 rounded-2xl border transition-colors flex items-start gap-3 cursor-pointer ${
                      n.is_read
                        ? "bg-white/[0.02] border-white/5 hover:bg-white/5"
                        : "bg-pink-500/[0.07] border-pink-400/20 hover:bg-pink-500/[0.12]"
                    }`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center border ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">{relativeTime(n.created_at)}</p>
                    </div>
                    {!n.is_read && <span className="shrink-0 w-2 h-2 rounded-full bg-pink-400 mt-2" />}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Delete this notification?")) deleteOne(n);
                      }}
                      aria-label="Delete notification"
                      className="shrink-0 w-7 h-7 grid place-items-center rounded-full text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`notification-delete-${n.notification_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </SwipeRow>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
